import alt from "../alt";
import indexBy from "lodash/keyBy";
import extend from "lodash/extend";
import values from "lodash/values";
import OrderUtil from "../utils/OrderUtil";
import OrderActions from "../actions/OrderActions";
import LedgerActions from "../actions/LedgerActions";
import UserStore from "../stores/UserStore";
import GiftCardUtil from "../utils/GiftCardUtil";

class OrderStore {
  constructor() {
    this.bindActions(OrderActions);

    this.state = {
      orders: {},
      lastOrderId: null,
      isFetchingMore: false,
      haveFetchedAll: false,
      resendConfirmationErrorMessage: null,
      refundErrorMessage: null,
      refundSuccess: false
    };

    this.exportPublicMethods({
      getOrdersForEventBucket: this.getOrdersForEventBucket.bind(this),
      getOrdersForPerson: this.getOrdersForPerson.bind(this)
    });
  }

  getOrdersForEventBucket(id) {
    return values(this.state.orders).filter(function (o) {
      return o.event_bucket.id === id;
    });
  }

  getOrdersForPerson(id, eventBucket) {
    return values(this.state.orders).filter(function (o) {
      if (eventBucket && o.event_bucket.id !== eventBucket) {
        return false;
      }
      return o.person && o.person.id === id;
    });
  }

  onReset() {
    this.setState({
      orders: {},
      lastOrderId: null,
      isFetchingMore: false,
      haveFetchedAll: false,
      resendConfirmationErrorMessage: null,
      refundErrorMessage: null
    });
  }

  onFetchAllForEventBucket(eventBucket) {
    OrderUtil.fetchAll(UserStore.getState().apiKey, eventBucket)
      .then(data => {
        OrderActions.updateOrders(indexBy(data, "id"));
      })
      .catch(OrderActions.fetchFailed);
  }

  onFetchAllForPerson(personId) {
    OrderUtil.fetchAllForPerson(UserStore.getState().apiKey, personId)
      .then(data => {
        OrderActions.updateOrders(indexBy(data, "id"));
      })
      .catch(OrderActions.fetchFailed);
  }

  onFetchAll() {
    OrderUtil.fetchAll(UserStore.getState().apiKey)
      .then(data => {
        OrderActions.updateOrders(indexBy(data, "id"));
      })
      .catch(OrderActions.fetchFailed);
  }

  onFetchOne(id) {
    OrderUtil.fetchOne(UserStore.getState().apiKey, id)
      .then(data => {
        OrderActions.updateOrder(data);
      })
      .catch(OrderActions.fetchFailed);
  }

  onFetchMore(data) {
    let { top, limit, eventBucket, godOrders } = data;

    this.setState({
      isFetchingMore: true
    });

    OrderUtil.fetchMore(
      UserStore.getState().apiKey,
      eventBucket,
      top ? null : this.state.lastOrderId,
      limit,
      { godOrders }
    )
      .then(data => {
        let { orders } = data,
          lastId = orders.length && orders[orders.length - 1].id;
        this.setState({
          lastOrderId: lastId,
          isFetchingMore: false,
          haveFetchedAll:
            data.reached_end ||
            (!top && (orders.length === 0 || lastId === this.state.lastOrderId))
        });
        OrderActions.updateOrders(indexBy(orders, "id"));
      })
      .catch(OrderActions.fetchFailed);
  }

  onEdit(newData) {
    OrderUtil.edit(UserStore.getState().apiKey, newData.id, newData.values)
      .then(data => {
        OrderActions.updateOrder(data);
      })
      .catch(OrderActions.editFailed);
  }

  onUpdateOrder(data) {
    this.state.orders[data.id] = data;
    this.setState(this.state);
  }

  onUpdateOrders(data) {
    if (!data) {
      this.state.orders = {};
    } else {
      extend(this.state.orders, data);
    }
    this.setState(this.state);
  }

  onResendConfirmation(options) {
    if (typeof options === "string") {
      options = { id: options };
    }

    OrderUtil.resendConfirmation(UserStore.getState().apiKey, options)
      .then(data => {
        data[options.bulk_person ? "bulkResent" : "confirmationResent"] =
          options.bulk_person || Date.now();
        OrderActions.updateOrder(data);
      })
      .catch(err => {
        this.state.orders[options.id][
          options.bulk_person ? "bulkResent" : "confirmationResent"
        ] = Date.now();
        this.state.resendConfirmationErrorMessage = err.message;
        this.setState(this.state);
      });
  }

  onResendGiftNotification(orderId) {
    GiftCardUtil.resendGiftNotification(UserStore.getState().apiKey, orderId)
      .then(() => {
        this.state.orders[orderId].confirmationResent = Date.now()
        this.setState(this.state)
      }).catch(err => {
        this.state.orders[orderId].confirmationResent = Date.now()
        this.state.resendConfirmationErrorMessage = err.message;
        this.setState(this.state);
    })
  }

  onResendConfirmationFailed(err) {
    this.setState({ resendConfirmationErrorMessage: err.message });
  }

  onRefund({ id, amount }) {
    OrderUtil.refund(UserStore.getState().apiKey, id, amount)
      .then(data => {
        OrderActions.updateOrder(data);
        LedgerActions.fetchLedgerTransactionsForOrder.defer(id); // force ledger update
        this.setState({ refundSuccess: true });
      })
      .catch(OrderActions.refundFailed);
  }

  onRefundFailed(err) {
    this.setState({ refundErrorMessage: err.message });
  }

  onDownloadReceipt(orderId) {
    OrderUtil.downloadReceipt(orderId);
  }

  onDownloadDonationReceipt(orderId) {
    OrderUtil.downloadDonationReceipt(orderId);
  }

  onClearErrors() {
    this.setState({
      resendConfirmationErrorMessage: null,
      refundErrorMessage: null,
      refundSuccess: false
    });
  }

  onFetchFailed(e) {
    alert(`Fetching order failed: ${e.message}`);
    throw e;
  }
}

export default alt.createStore(OrderStore, "OrderStore");



// WEBPACK FOOTER //
// ./src/app/stores/OrderStore.js